import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import ButtonBase from "@material-ui/core/ButtonBase";
import PropTypes from "prop-types";
import IconButton from "@material-ui/core/IconButton";
import clsx from "clsx";
import {useBreakpoints} from "../../../hooks/useBreakpoints";
import CheckIcon from "../streamline-icon/CheckIcon";
import AccessibleIcon from "../streamline-icon/AccessibleIcon";
import { Box, useTheme } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    paper: {
        display: "flex",
        cursor: "pointer",
        "@media (hover: none)": {
            "&:hover": {
                backgroundColor: "none"
            },
        },
        "@media (hover: hover)": {
            "&:hover": {
                backgroundColor: theme.palette.secondary["50"]
            },
        },
        "&:focus": {
            backgroundColor: theme.palette.secondary["50"]
        },
        borderRadius: 0
    },
    selected: {
        backgroundColor: theme.palette.primary["50"],
        "@media (hover: hover)": {
            "&:hover": {
                backgroundColor: theme.palette.primary["100"]
            },
        },
    },
    paperPadding: {
        padding: theme.spacing(1),
    },
    paperPaddingMobile: {
        padding: theme.spacing(1),
    },
    paperText: {
        marginTop: theme.spacing(0.45),
    },
    image: {
        width: 87,
        height: 48,
        borderRadius: theme.spacing(0.5)
    },
    img: {
        textAlign: "left",
        height: "100%"
    },
    iconCheck: {
        color: theme.palette.primary["100"],
        float: "right"
    },
    iconCheckSelected: {
        color: theme.palette.primary.main
    },
    marginTop: {
        marginTop: theme.spacing(1.375)
    },
    marginLeft2: {
        marginLeft: theme.spacing(2)
    },
    marginRightMinusHalf: {
        marginRight: -theme.spacing(0.5)
    },
    width: {
        minWidth: 300
    },
    widthTypography: {
        width: 300,
    },
    float: {
        float: "right"
    },
    flexWrapNoWrap: {
        flexWrap: "nowrap"
    },
    hoverBackgroundUnset: {
        "&:hover": {
            background: "unset"
        }
    },
    textEllipsis: {
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden"
    },
    width100: {
        width: "100%"
    },
    gridColumnsUnset: {
        width: "fit-content",
        flexBasis: "unset"
    },
    accessibleIcon: {
        color: theme.palette.text.white,
        fontSize: "12px"
    },
    flexCenter: {
        display: "flex",
        alignItems: "center"
    }
}));

const DEFAULT_IMAGE = "https://images.ctfassets.net/tzyjn3z7mm9b/4isfHkZWYMic50grgsOSIB/8766cba78527afb497384234be0f78a0/1002_aHAP015487I.jpg";

const SearchResortPaper = ({
    title,
    subtitle,
    imageUrl,
    selected,
    onClick,
    wheelchairAccessibility
}) => {

    const classes = useStyles();
    const theme = useTheme();
    const {isExtraSmallScreen, isMediumScreen} = useBreakpoints();

    const handleClick = () => {
        if (onClick) {
            onClick();
        }
    };

    return (
        <div className={classes.root}>

            <Paper
                className={clsx(
                    classes.paper,
                    {[classes.selected]: selected},
                    {[classes.paperPadding]: !isExtraSmallScreen()},
                    {[classes.paperPaddingMobile]: isExtraSmallScreen()}
                )}
                elevation={0}
                onClick={handleClick}
            >

                <Grid
                    container
                    direction={"row"}
                    className={clsx({[classes.width]: isMediumScreen()})}
                    justify={"space-between"}
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (event.keyCode === 13 || event.keyCode === 32) {
                            event.preventDefault();
                            event.target.click();
                        }
                    }}
                >

                    <Grid
                        item
                        container
                        direction={"row"}
                        xs={10}
                        className={classes.flexWrapNoWrap}
                    >
                        <Grid
                            item
                            xs={4}
                            md={5}
                            lg={4}
                            className={classes.gridColumnsUnset}
                        >
                            <ButtonBase
                                className={classes.img}
                                tabIndex={-1}
                            >
                                <img
                                    className={classes.image}
                                    alt="complex"
                                    src={imageUrl + "?fit=fill&w=174&h=96&fm=webp&q=85" || DEFAULT_IMAGE + "?w=174&h=96&fm=webp&q=85"}
                                />
                            </ButtonBase>
                        </Grid>

                        <Grid
                            item
                            xs={8}
                            md={7}
                            lg={8}
                            container
                            direction={"column"}
                            justify={!subtitle ? "center" : "space-evenly"}
                            alignItems={"flex-start"}
                            className={classes.marginLeft2}
                        >
                            <Grid className={classes.width100}>
                                <Typography
                                    variant="subtitle1"
                                    className={classes.textEllipsis}
                                >
                                    {title}
                                </Typography>
                            </Grid>

                            <Grid className={clsx(classes.width100, wheelchairAccessibility ? classes.flexCenter : "")}>
                                {
                                    wheelchairAccessibility && (
                                        <Box
                                            display={"flex"}
                                            justifyContent={"center"}
                                            width={"18px"}
                                            height={"18px"}
                                            borderRadius={"4px"}
                                            padding={"2px"}
                                            bgcolor={theme.palette.text.link}
                                            marginRight={"4px"}
                                        >
                                            <AccessibleIcon className={classes.accessibleIcon} />
                                        </Box>
                                    )
                                }
                                <Typography
                                    variant="body2"
                                    className={classes.textEllipsis}
                                >
                                    {subtitle}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>

                    {selected && (
                        <Grid
                            item
                            container
                            className={clsx(classes.paperText, {[classes.widthTypography]: isMediumScreen()})}
                            xs={2}
                            justify={"flex-end"}
                            alignItems={"center"}
                        >
                            <IconButton
                                edge="end"
                                aria-label="check"
                                className={clsx(classes.marginRightMinusHalf, classes.hoverBackgroundUnset, {[classes.float]: !isMediumScreen()})}
                                disableRipple
                                tabIndex={-1}
                            >
                                <CheckIcon
                                    className={clsx(classes.iconCheck, {[classes.iconCheckSelected]: selected})}
                                />
                            </IconButton>
                        </Grid>
                    )}
                </Grid>

            </Paper>

        </div>
    );
};

SearchResortPaper.defaultProps = {
    selected: false,
    title: "",
    subtitle: "",
    imageUrl: "",
    wheelchairAccessibility: false
};

SearchResortPaper.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.node,
    imageUrl: PropTypes.string,
    selected: PropTypes.bool,
    onClick: PropTypes.func,
    wheelchairAccessibility: PropTypes.bool,
};

export default SearchResortPaper;
